import React, { FC } from 'react'
import { Redirect, Route, Switch, useParams } from 'react-router-dom'

// Components
import ArtboardSlugRedirect from 'modules/shares/ArtboardSlugRedirect'
import { DetailViewQuery } from 'modules/shares/DetailView'
import DocumentView from 'modules/shares/DocumentView'
import PageCanvasView from 'modules/shares/PageCanvasView'
import PrototypeView from 'modules/shares/PrototypeView'
import { ShareVersionProvider } from 'modules/versioning'
import NotFoundView from 'views/NotFoundView'
import ArtboardRedirect from './ArtboardRedirect'
import { ShareAuthorizerQueryData } from './useShareAuthorizerQuery'

// Types
import { useGetUserCredentialsQuery } from 'gqlTypes'

// Hooks
import { useCommentCreatedSubscriptionWrapper } from 'modules/activity/operations/useCommentCreatedSubscriptionWrapper'

// Constants
import {
  getAllTemplatesForVersionedRoute,
  routes,
  RouteParams,
} from 'constants/routes'
import { CWV_ROUTES } from '../ComponentsWebView'

// Utils
import { ContainerRefScrollProvider } from 'utils/ContainerRefScrollContext'
import ArtboardUuidRedirect from './ArtboardUuidRedirect'

type ShareMainRoutesProps = Pick<ShareAuthorizerQueryData, 'share' | 'refetch'>

const ShareMainRoutes: FC<ShareMainRoutesProps> = ({ share, refetch }) => {
  const { data } = useGetUserCredentialsQuery({
    fetchPolicy: 'cache-only',
  })

  const { shareID } = useParams<RouteParams<'SHARE_VIEW'>>()

  const authToken = data?.userCredentials?.authToken
  const { version, ...shareBase } = share

  // Allows to see a new comment or reply in real time
  useCommentCreatedSubscriptionWrapper({
    shareIdentifier: shareBase.identifier!,
    isAuthenticated: !!authToken,
  })

  return (
    <ShareVersionProvider
      latestVersion={version!}
      share={shareBase}
      onVersionUpdateNeeded={async () => {
        await refetch()
      }}>
      <ContainerRefScrollProvider>
        <Switch>
          {/* Prototype view */}
          <Route
            exact
            path={getAllTemplatesForVersionedRoute('PROTOTYPE')}
            render={props => (
              <PrototypeView
                key={props?.match.params?.versionShortId}
                {...props}
              />
            )}
          />

          {/* Artboard detail view based on UUID (redirect to ARTBOARD_DETAIL) */}
          <Route
            exact
            path={[...getAllTemplatesForVersionedRoute('ARTBOARD_DETAIL_UUID')]}
            component={ArtboardUuidRedirect}
          />

          {/* Artboard detail view */}
          <Route
            exact
            path={[
              ...getAllTemplatesForVersionedRoute('ARTBOARD_REVISION'),
              ...getAllTemplatesForVersionedRoute('ARTBOARD_DETAIL'),
            ]}
            component={DetailViewQuery}
          />

          <Route
            exact
            path={[
              ...getAllTemplatesForVersionedRoute('SHARE_PAGE_CANVAS_VIEW'),
            ]}
            component={PageCanvasView}
          />

          {/* Share root, including any CWV (Components Web View) route */}
          <Route
            exact
            path={[
              ...getAllTemplatesForVersionedRoute('SHARE_VIEW'),
              ...getAllTemplatesForVersionedRoute('SHARE_PAGE_VIEW'),
              ...CWV_ROUTES,
            ]}
            component={DocumentView}
          />

          {/* Legacy routes redirections
          LEGACY_ARTBOARD_DETAIL (s/:shareID/:artboardShortId) is conflicting
          any CWV route (s/:shareID/symbols), and LEGACY_ARTBOARD_SLUG is
          conflicting with any CWV versioned route, so it needs to be at
          the bottom of the switch because the order matters */}
          <Route
            exact
            path={[
              routes.LEGACY_ARTBOARD_SLUG.template(),
              routes.LEGACY_PROTOTYPE_SLUG.template(),
            ]}
            component={ArtboardSlugRedirect}
          />

          <Route
            exact
            path={[
              routes.LEGACY_PROTOTYPE.template(),
              routes.LEGACY_ARTBOARD_DETAIL.template(),
            ]}
            component={ArtboardRedirect}
          />

          {/* Catch the rest of the urls, redirect unknown routes to the root
          of the share. Have in mind that urls with 3 params like
          `s/:shareID/whatever` will be taken by LEGACY_ARTBOARD_DETAIL, and
           urls with 4 params will be taken by LEGACY_ARTBOARD_SLUG */}
          <Route path={routes.SHARE_VIEW.template()}>
            <Redirect to={routes.SHARE_VIEW.create({ shareID })} />
          </Route>

          {/* We should never reach this point, because of the previous redirection */}
          <Route component={NotFoundView} />
        </Switch>
      </ContainerRefScrollProvider>
    </ShareVersionProvider>
  )
}

export default ShareMainRoutes
